import {Col, Grid, Row} from '@jetbrains/ring-ui/components/grid/grid'
import {skipToken} from '@reduxjs/toolkit/query'
import type {ReactElement} from 'react'
import React from 'react'

import {useAppSelector} from '../../../../hooks/react-redux'
import {getExtensionEndpoint} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildTypeId, ProjectId, BuildId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import Icon from '../../../common/Icon/Icon'
import Loader from '../../../common/Loader/Loader'
import {processGroups} from '../CompareBuildsPage.selectors'
import type {CompareBuildsLine} from '../CompareBuildsPage.types'
import OnlyChangedToggle from '../OnlyChangedToggle/OnlyChangedToggle.container'
import CompareBuildsRow from '../rows/CompareBuildsRow'

import CompareBuildsFilter from './CompareBuildsFilter/CompareBuildsFilter'
import TestsTableHeader from './TestsTableHeader/TestsTableHeader'

import styles from './CompareBuildsTable.css'

const API_LIMIT = 1001
type Props = {
  type: string
  buildTypeId: BuildTypeId | null | undefined
  showChangedOnly: boolean
  filterText: string | null | undefined
  changeFilterText: (filterText: string) => unknown
  sourceId: BuildId
  compareWithId: BuildId | null | undefined
  sourceProjectId: ProjectId | null | undefined
  compareProjectId: ProjectId | null | undefined
  sourceBuildNumber: string | null | undefined
  compareBuildNumber: string | null | undefined
  wide: boolean
}

function CompareBuildsTable({
  buildTypeId,
  showChangedOnly,
  type,
  filterText,
  changeFilterText,
  sourceId,
  compareWithId,
  sourceProjectId,
  compareProjectId,
  sourceBuildNumber,
  compareBuildNumber,
  wide,
}: Props) {
  const endpoint = useAppSelector(state => getExtensionEndpoint(state, 'builds-diff')?.endpoint)
  const {info} = restApi.endpoints.getCompareBuildsInfo.useQuery(
    endpoint != null && compareWithId != null
      ? {
          endpoint,
          compareType: type,
          sourceId,
          compareWithId,
          mode: showChangedOnly ? 'changed' : 'all',
          filterText,
        }
      : skipToken,
    {selectFromResult: ({data}) => ({info: data})},
  )

  const lines = React.useMemo(
    () =>
      info?.groups == null || info.groups.length === 0 ? emptyArray : processGroups(info.groups, 0),
    [info],
  )

  const valuesCount = React.useMemo(
    () =>
      lines.reduce(
        (acc: number, line: CompareBuildsLine) => (line.type !== 'title' ? acc + 1 : acc),
        0,
      ),
    [lines],
  )
  const noDifferencesMessage = React.useMemo(() => {
    switch (type) {
      case 'params':
        return 'No differences between build parameters were found'
      case 'dependencies':
        return 'No differences in dependencies were found'
      case 'revisions':
        return 'No differences between revisions were found'
      case 'statistics':
        return 'No differences in statistics were found'
      case 'tests':
        return 'No differences between test results were found'
      default:
        return 'No differences were found'
    }
  }, [type])
  const limited = valuesCount === API_LIMIT
  const empty = valuesCount === 0
  const filtered = (filterText != null && filterText.length > 0) || showChangedOnly

  if (info == null) {
    return <Loader />
  }

  let results

  if (empty) {
    results = (
      <div className={styles.empty}>
        <Icon name="search-empty" className={styles.emptyIcon} />
        {` ${noDifferencesMessage}`}
      </div>
    )
  } else {
    const rows: ReactElement[] = []
    lines.forEach((line: CompareBuildsLine, index: number) => {
      if (!limited || index < API_LIMIT) {
        rows.push(
          <CompareBuildsRow
            key={index} /* eslint-disable-line react/no-array-index-key */
            line={line}
            showChangedOnly={showChangedOnly}
            buildTypeId={buildTypeId}
            sourceProjectId={sourceProjectId}
            compareProjectId={compareProjectId}
            sourceBuildNumber={sourceBuildNumber}
            compareBuildNumber={compareBuildNumber}
            wide={wide}
          />,
        )
      }
    })
    results = (
      <>
        {type === 'tests' && (
          <TestsTableHeader
            sourceBuildNumber={sourceBuildNumber}
            compareBuildNumber={compareBuildNumber}
          />
        )}
        {rows}
        {limited && (
          <div className={styles.limitationMessage}>{`Displaying the first ${
            API_LIMIT - 1
          } results. Use filters to narrow the request.`}</div>
        )}
      </>
    )
  }

  return (
    <Grid className={styles.grid}>
      {(!empty || filtered) && (
        <Row>
          <Col xs={12} className={styles.controls}>
            <OnlyChangedToggle />
            <CompareBuildsFilter
              type={type}
              className={styles.filter}
              filterText={filterText ?? ''}
              changeFilterText={changeFilterText}
              resultsCount={valuesCount}
              resultsLimited={limited}
            />
          </Col>
        </Row>
      )}
      {results}
    </Grid>
  )
}

export default CompareBuildsTable
